import React, { useState, useEffect } from 'react';
import axiosClient from '../services/axiosClient';
import './ResumesPage.css';

const ResumesPage = () => {
  const [userName, setUserName] = useState('');
  const [userInitials, setUserInitials] = useState('');
  // Simple local CV manager state
  const [resumes, setResumes] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(new Set());
  const fileInputRef = React.useRef(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [showUploader, setShowUploader] = useState(false);
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [errorResumes, setErrorResumes] = useState('');
  // Upload form state
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeName, setResumeName] = useState('');
  const [countryCode, setCountryCode] = useState('SN');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  // URLs helpers (component scope)
  const API_BASE = process.env.REACT_APP_API_BASE || '';
  const SITE_BASE = API_BASE.replace(/\/api\/?$/, '');
  const toAbsoluteUrl = (u) => {
    if (!u) return '';
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith('/')) return `${SITE_BASE}${u}`;
    return `${SITE_BASE}/${u}`;
  };
  // Delete state
  const [deleting, setDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  // Edit form state
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState('');
  const [editResume, setEditResume] = useState(null);
  const [editName, setEditName] = useState('');
  const [editFile, setEditFile] = useState(null);
  const [editSelectedFileName, setEditSelectedFileName] = useState('');
  const editFileInputRef = React.useRef(null);

  useEffect(() => {
    // Helper: tenter plusieurs clés connues et formats possibles
    const parseFromStorage = (storage) => {
      const keys = ['userData', 'user', 'userInfo', 'currentUser'];
      for (const key of keys) {
        const data = storage.getItem(key);
        if (data) {
          try {
            const parsed = JSON.parse(data);
            if (parsed && (parsed.name || parsed.username || parsed.email)) {
              return parsed;
            }
          } catch (e) {
            // Si ce n'est pas du JSON, essayer comme string
            if (data.includes('@') || data.length > 2) {
              return { name: data };
            }
          }
        }
      }
      return null;
    };

    const updateUserFromStorage = () => {
      const userData = parseFromStorage(localStorage) || parseFromStorage(sessionStorage);
      
      if (userData) {
        const name = userData.name || userData.username || userData.email || 'Consultant';
        setUserName(name);
        
        // Générer les initiales
        const nameParts = name.split(' ');
        let initials = '';
        if (nameParts.length >= 2) {
          initials = nameParts[0].charAt(0) + nameParts[1].charAt(0);
        } else {
          initials = name.charAt(0) + (name.charAt(1) || '');
        }
        setUserInitials(initials.toUpperCase());
      } else {
        setUserName('Consultant');
        setUserInitials('CO');
      }
    };

    updateUserFromStorage();

    // Écouter les changements de localStorage
    const onStorage = (e) => {
      if (['userData', 'user', 'userInfo', 'currentUser'].includes(e.key)) {
        updateUserFromStorage();
      }
    };
    window.addEventListener('storage', onStorage);

    // Écouter le focus de la fenêtre pour rafraîchir
    const onFocus = () => updateUserFromStorage();
    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  // Extract auth token from storage
  const getAuth = () => {
    const storages = [window.localStorage, window.sessionStorage];
    const keys = ['authData','auth','userData','senoffre_auth','loginData','currentUser','user'];
    for (const storage of storages) {
      for (const key of keys) {
        try {
          const raw = storage.getItem(key);
          if (!raw) continue;
          const data = JSON.parse(raw);
          if (data?.token) return { token: data.token, tokenType: data.tokenType || 'Bearer' };
        } catch(_) { /* ignore */ }
      }
      // Fallback: scan all keys for a JSON containing 'token'
      try {
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (!k) continue;
          const raw = storage.getItem(k);
          if (!raw) continue;
          try {
            const data = JSON.parse(raw);
            if (data?.token) return { token: data.token, tokenType: data.tokenType || 'Bearer' };
          } catch (_) { /* non-JSON, skip */ }
        }
      } catch(_) { /* ignore */ }
    }
    // fallback: store under 'token'
    const tokenOnly = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (tokenOnly) return { token: tokenOnly, tokenType: 'Bearer' };
    return null;
  };

  // Load resumes from API
  const loadResumes = React.useCallback(async () => {
    setLoadingResumes(true);
    setErrorResumes('');
    try {
      const auth = getAuth();
      const res = await axiosClient.get('/resumes', {
        params: {
          belongLoggedUser: 1,
          perPage: 10,
        },
        headers: {
          'Accept': 'application/json',
          ...(auth?.token ? { 'Authorization': `${auth.tokenType || 'Bearer'} ${auth.token}` } : {}),
        },
      });
      const payload = res.data;
      const root = payload?.result || payload || {};
      let list = root?.data || payload?.data || [];
      if (!Array.isArray(list) && Array.isArray(list?.data)) list = list.data;
      const mapped = (Array.isArray(list) ? list : []).map((r) => {
        const fileCandidate = r.url || r.file_url || r.download_url || r.filepath || r.path || '';
        let finalUrl = fileCandidate;
        if (!finalUrl && typeof r.filename === 'string') {
          const fn = r.filename;
          const looksLikePath = fn.includes('/') || /\.(pdf|docx?|rtf|pptx?|od[tp]|jpe?g|bmp|png)$/i.test(fn);
          if (looksLikePath) finalUrl = fn;
        }
        return {
          id: r.id || r.uuid || r.slug || String(Math.random()),
          name: r.name || r.filename || r.title || 'Liasse',
          url: toAbsoluteUrl(finalUrl),
          updatedAt: r.updated_at_formatted || r.updated_at || r.created_at_formatted || r.created_at || '',
          size: r.size_formatted || r.size || '',
        };
      });
      setResumes(mapped);
    } catch (e) {
      setErrorResumes("Impossible de charger votre Liasse. Assurez-vous d'être connecté.");
    } finally {
      setLoadingResumes(false);
    }
  }, []);

  useEffect(() => {
    loadResumes();
  }, [loadResumes]);

  // Handlers for CV manager
  const filteredResumes = resumes.filter(r => r.name.toLowerCase().includes(query.toLowerCase()));
  const allChecked = filteredResumes.length > 0 && filteredResumes.every(r => selected.has(r.id));
  const toggleAll = (checked) => {
    const next = new Set(selected);
    if (checked) {
      filteredResumes.forEach(r => next.add(r.id));
    } else {
      filteredResumes.forEach(r => next.delete(r.id));
    }
    setSelected(next);
  };
  const toggleOne = (id, checked) => {
    const next = new Set(selected);
    if (checked) next.add(id); else next.delete(id);
    setSelected(next);
  };
  const onDeleteSelected = () => {
    if (selected.size === 0 || deleting) return;
    if (!window.confirm(`Confirmer la suppression de ${selected.size} Liasse(s) ?`)) return;
    const ids = Array.from(selected);
    (async () => {
      try {
        setDeleting(true);
        const auth = getAuth();
        const url = `/resumes/${encodeURIComponent(ids.join(','))}`;
        await axiosClient.delete(url, {
          headers: {
            'Accept': 'application/json',
            ...(auth?.token ? { 'Authorization': `${auth.tokenType || 'Bearer'} ${auth.token}` } : {}),
          },
        });
        await loadResumes();
        setSelected(new Set());
      } catch (e) {
        alert("Échec de la suppression. Réessayez.");
      } finally {
        setDeleting(false);
      }
    })();
  };
  const onUploadClick = () => fileInputRef.current?.click();
  const onUploadFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResumeFile(file);
    setSelectedFileName(file.name);
    e.target.value = '';
  };
  const onSubmitUpload = (e) => {
    e.preventDefault();
    setUploadError('');
    if (!resumeFile) {
      setUploadError('Veuillez sélectionner un fichier.');
      return;
    }
    (async () => {
      try {
        setUploading(true);
        const auth = getAuth();
        const form = new FormData();
        form.append('resume[country_code]', countryCode || 'SN');
        form.append('resume[name]', resumeName || selectedFileName || 'Liasse');
        form.append('resume[filename]', resumeFile);
        await axiosClient.post('/resumes', form, {
          headers: {
            ...(auth?.token ? { 'Authorization': `${auth.tokenType || 'Bearer'} ${auth.token}` } : {}),
            'Accept': 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        });
        // Refresh list and reset form
        await loadResumes();
        setShowUploader(false);
        setResumeFile(null);
        setSelectedFileName('');
        setResumeName('');
      } catch (err) {
        setUploadError("Échec de l'upload du Liasse. Réessayez.");
      } finally {
        setUploading(false);
      }
    })();
  };

  // Open edit panel with selected resume
  const onOpenEdit = (r) => {
    setEditResume(r);
    setEditName(r?.name || '');
    setEditFile(null);
    setEditSelectedFileName('');
    setShowUploader(false);
    setShowEditor(true);
    setEditError('');
  };

  const onEditFileClick = () => editFileInputRef.current?.click();
  const onEditFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setEditFile(f);
    setEditSelectedFileName(f.name);
    e.target.value = '';
  };

  const onSubmitEdit = (e) => {
    e.preventDefault();
    if (!editResume) return;
    (async () => {
      try {
        setEditing(true);
        setEditError('');
        const auth = getAuth();
        const form = new FormData();
        form.append('resume[name]', editName || editResume.name || 'Liasse');
        if (editFile) {
          form.append('resume[filename]', editFile);
        }
        await axiosClient.post(`/resumes/${encodeURIComponent(editResume.id)}`, form, {
          headers: {
            ...(auth?.token ? { 'Authorization': `${auth.tokenType || 'Bearer'} ${auth.token}` } : {}),
            'Accept': 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        });
        await loadResumes();
        setShowEditor(false);
        setEditResume(null);
        setEditName('');
        setEditFile(null);
        setEditSelectedFileName('');
      } catch (err) {
        setEditError("Échec de la mise à jour du liasse. Réessayez.");
      } finally {
        setEditing(false);
      }
    })();
  };

  return (
    <div className="consultant-layout">

      {/* Modern Main Content */}
      <div className="modern-content-body">
        <div className="modern-content-container">
          <div className="modern-dashboard-content">
            <div className="modern-cvs-container">
              {/* Hero Section (hidden as requested) */}
              <div className="modern-cvs-hero" style={{display:'none'}}>
                <div className="hero-background">
                  <div className="hero-pattern"></div>
                  <div className="hero-gradient"></div>
                </div>
                <div className="hero-content">
                  <div className="hero-badge">
                    <i className="fa fa-file-text me-2"></i>
                    Gestion des Liasses
                  </div>
                  <h1 className="hero-title">
                    Mes <span className="gradient-text">Curriculum Vitae</span>
                    <div className="title-decoration"></div>
                  </h1>
                  <p className="hero-subtitle">
                    Créez, gérez et optimisez votre Liasse pour maximiser vos chances de décrocher l'Appel d'offres de vos rêves
                  </p>
                  <div className="hero-stats">
                    <div className="stat-item">
                      <span className="stat-number">3</span>
                      <span className="stat-label">Liasse Actifs</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">12</span>
                      <span className="stat-label">Candidatures</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">85%</span>
                      <span className="stat-label">Taux de réponse</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Bar */}
              <div className="cvs-action-bar">
                <div className="action-left">
                  <div className="cvs-count-badge">
                    <i className="fa fa-file-text me-2"></i>
                    <span>{resumes.length} Liasse{resumes.length>1?'s':''} disponible{resumes.length>1?'s':''}</span>
                  </div>
                  <h2 className="section-title">
                    votre Offre
                    <div className="title-underline"></div>
                  </h2>
                </div>
                <div className="action-right">
                  <div className="view-options">
                    <button className="view-btn active" data-view="grid">
                      <i className="fa fa-th-large"></i>
                    </button>
                    <button className="view-btn" data-view="list">
                      <i className="fa fa-list"></i>
                    </button>
                  </div>
                  <button className="create-cv-btn" onClick={() => setShowUploader(true)}>
                    <i className="fa fa-plus me-2"></i>
                    Ajouter Liasse
                  </button>
                  <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.rtf,.rtx,.ppt,.pptx,.odt,.odp,.wps,.jpeg,.jpg,.bmp,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={onUploadFile} hidden />
                </div>
              </div>

              {/* Uploader panel */}
              {showUploader && (
                <div className="card shadow-sm border-0 mb-4" style={{borderRadius: 16}}>
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h5 className="mb-0 d-flex align-items-center gap-2">
                        <i className="fa fa-paperclip text-success"></i>
                        Ajouter une offre
                      </h5>
                      <button className="btn btn-sm btn-outline-secondary" onClick={()=>setShowUploader(false)}>Mes Liasses</button>
                    </div>
                    <div className="card" style={{borderRadius: 12}}>
                      <div className="card-header bg-white">
                        <span className="text-success fw-semibold">Liasse</span>
                      </div>
                      <div className="card-body">
                        <form onSubmit={onSubmitUpload}>
                          <div className="row align-items-center g-2">
                            <label className="col-sm-2 col-form-label">Votre Liasse</label>
                            <div className="col-sm-10">
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Sélectionner le(s) fichier ..."
                                  readOnly
                                  onClick={onUploadClick}
                                  value={selectedFileName}
                                />
                                <button type="button" className="btn btn-success" onClick={onUploadClick}>
                                  <i className="fa fa-folder-open me-2"></i> Parcourir ...
                                </button>
                              </div>
                              <div className="form-text">
                                Types de fichier: pdf, doc, docx, word, rtf, rtx, ppt, pptx, odt, odp, wps, jpeg, jpg, bmp, png
                                joindre ou importer un ensemble de documents en une seule fois.
                              </div>
                            </div>
                          </div>
                          <div className="mt-3">
                            <button type="submit" className="btn btn-success">Soumettre</button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Editor panel */}
              {showEditor && editResume && (
                <div className="card shadow-sm border-0 mb-4" style={{borderRadius: 16}}>
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h5 className="mb-0 d-flex align-items-center gap-2">
                        <i className="fa fa-paperclip text-success"></i>
                        Editer le Liasse
                      </h5>
                      <button className="btn btn-sm btn-outline-secondary" onClick={()=>setShowEditor(false)}>Mes Liasses</button>
                    </div>
                    <div className="card" style={{borderRadius: 12}}>
                      <div className="card-header bg-white">
                        <span className="text-success fw-semibold">Liasse</span>
                      </div>
                      <div className="card-body">
                        {editError && <div className="alert alert-danger py-2">{editError}</div>}
                        <form onSubmit={onSubmitEdit}>
                          <div className="mb-3 row align-items-center g-2">
                            <label className="col-sm-2 col-form-label">Nom</label>
                            <div className="col-sm-10">
                              <input type="text" className="form-control" value={editName} onChange={(e)=>setEditName(e.target.value)} />
                            </div>
                          </div>
                          <div className="row align-items-center g-2">
                            <label className="col-sm-2 col-form-label">Votre Liasse</label>
                            <div className="col-sm-10">
                              <div className="input-group">
                                <input type="text" className="form-control" placeholder="Sélectionner le(s) fichier ..." readOnly onClick={onEditFileClick} value={editSelectedFileName} />
                                <button type="button" className="btn btn-success" onClick={onEditFileClick}>
                                  <i className="fa fa-folder-open me-2"></i> Parcourir ...
                                </button>
                              </div>
                              <div className="form-text">Types de fichier: pdf, doc, docx, word, rtf, rtx, ppt, pptx, odt, odp, wps, jpeg, jpg, bmp, png
                              joindre ou importer un ensemble de documents en une seule fois.
                              </div>
                              {editResume?.url && (
                                <div className="mt-3">
                                  <a href={editResume.url} target="_blank" rel="noreferrer" className="btn btn-outline-secondary">
                                    <i className="fa fa-download me-2"></i> Télécharger
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="mt-3">
                            <button type="submit" className="btn btn-success" disabled={editing}>{editing ? 'Mise à jour…' : 'Mettre à jour'}</button>
                          </div>
                        </form>
                        <input ref={editFileInputRef} type="file" accept=".pdf,.doc,.docx,.rtf,.rtx,.ppt,.pptx,.odt,.odp,.wps,.jpeg,.jpg,.bmp,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={onEditFileChange} hidden />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Modern Manager (Table style) */}
              <div className="card shadow-sm border-0 mb-4" style={{borderRadius: 16}}>
                <div className="card-body">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <button className="btn btn-success" onClick={() => toggleAll(!allChecked)}>
                        <i className="fa fa-check-double me-2"></i> Sélectionner tout
                      </button>
                      <button className="btn btn-outline-danger" onClick={onDeleteSelected} disabled={selected.size===0 || deleting}>
                        <i className="fa fa-trash me-2"></i> Supprimer
                      </button>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <label className="text-muted small mb-0">Rechercher</label>
                      <input className="form-control" style={{minWidth:220}} value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="Nom du Liasse..." />
                      {query && (
                        <button className="btn btn-link text-success" onClick={()=>setQuery('')}>effacer</button>
                      )}
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th style={{width:40}}>
                            <input type="checkbox" className="form-check-input" checked={allChecked} onChange={(e)=>toggleAll(e.target.checked)} />
                          </th>
                          <th style={{width:140}}>Fichier</th>
                          <th>Nom</th>
                          <th style={{width:200}} className="text-end">Option</th>
                        </tr>
                      </thead>
                      <tbody>
                      {loadingResumes && (
                        <tr><td colSpan="4" className="text-muted">Chargement…</td></tr>
                      )}
                      {errorResumes && !loadingResumes && (
                        <tr><td colSpan="4" className="text-danger">{errorResumes}</td></tr>
                      )}
                      {!loadingResumes && !errorResumes && filteredResumes.map(r => (
                        <tr key={r.id} className="bg-light-subtle">
                          <td>
                            <input type="checkbox" className="form-check-input" checked={selected.has(r.id)} onChange={(e)=>toggleOne(r.id, e.target.checked)} />
                          </td>
                          <td>
                            {r.url && r.url !== '#' ? (
                              <a href={r.url} className="btn btn-outline-secondary btn-sm" target="_blank" rel="noreferrer">
                                <i className="fa fa-download me-2"></i> Télécharger
                              </a>
                            ) : (
                              <button className="btn btn-outline-secondary btn-sm" disabled>
                                <i className="fa fa-download me-2"></i> Télécharger
                              </button>
                            )}
                          </td>
                          <td className="fw-medium">{r.name}</td>
                          <td className="text-end">
                            <div className="btn-group" role="group">
                              <button className="btn btn-success btn-sm" onClick={()=>onOpenEdit(r)}><i className="fa fa-edit me-1"></i>Éditer</button>
                              <button className="btn btn-danger btn-sm" disabled={deleting || deletingIds.has(r.id)} onClick={() => {
                                if (deleting) return;
                                if (!window.confirm('Confirmer la suppression de ce Liasse ?')) return;
                                setDeletingIds(prev => new Set(prev).add(r.id));
                                (async () => {
                                  try {
                                    const auth = getAuth();
                                    const url = `${process.env.REACT_APP_API_BASE || ''}/resumes/${encodeURIComponent(r.id)}`;
                                    const res = await fetch(url, {
                                      method: 'DELETE',
                                      headers: {
                                        'Accept': 'application/json',
                                        ...(auth?.token ? { 'Authorization': `${auth.tokenType || 'Bearer'} ${auth.token}` } : {}),
                                      },
                                    });
                                    if (!res.ok) throw new Error(`Erreur ${res.status}`);
                                    await loadResumes();
                                  } catch (e) {
                                    alert("Échec de la suppression. Réessayez.");
                                  } finally {
                                    setDeletingIds(prev => { const n = new Set(prev); n.delete(r.id); return n; });
                                  }
                                })();
                              }}><i className="fa fa-trash me-1"></i>Supprimer</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!loadingResumes && !errorResumes && filteredResumes.length === 0 && (
                        <tr>
                          <td colSpan="4" className="text-muted text-center py-4">Aucun Liasse</td>
                        </tr>
                      )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div style={{display:'none'}}>
              {/* CV Card 1 - Template Moderne */}
              <div className="cv-card">
                <div className="cv-card-header">
                  <div className="cv-preview modern">
                    <div className="cv-preview-content">
                      <div className="cv-header-preview">
                        <div className="cv-avatar">BG</div>
                        <div className="cv-info">
                          <div className="cv-name">Babacar GUEYE</div>
                          <div className="cv-title">Développeur Full Stack</div>
                                    <div className="cv-info">
                                        <div className="cv-name">Babacar GUEYE</div>
                                        <div className="cv-title">Designer UI/UX</div>
                                    </div>
                                </div>
                                <div className="cv-sections creative">
                                    <div className="cv-section"></div>
                                    <div className="cv-section"></div>
                                    <div className="cv-section short"></div>
                                </div>
                            </div>
                        </div>
                        <div className="cv-badges">
                            <span className="cv-badge warning">Brouillon</span>
                        </div>
                    </div>
                    <div className="cv-card-body">
                        <div className="cv-meta">
                            <h3 className="cv-name">Liasse Créatif Design</h3>
                            <p className="cv-description">Template créatif pour postes de design</p>
                            <div className="cv-stats">
                                <div className="cv-stat">
                                    <i className="fa fa-eye"></i>
                                    <span>89 vues</span>
                                </div>
                                <div className="cv-stat">
                                    <i className="fa fa-download"></i>
                                    <span>3 téléchargements</span>
                                </div>
                                <div className="cv-stat">
                                    <i className="fa fa-calendar"></i>
                                    <span>Créé il y a 1 semaine</span>
                                </div>
                            </div>
                        </div>
                        <div className="cv-actions">
                            <button className="cv-action-btn primary">
                                <i className="fa fa-edit"></i>
                                Modifier
                            </button>
                            <button className="cv-action-btn secondary">
                                <i className="fa fa-download"></i>
                                Télécharger
                            </button>
                            <div className="cv-more-actions">
                                <button className="cv-more-btn">
                                    <i className="fa fa-ellipsis-v"></i>
                                </button>
                                <div className="cv-dropdown">
                                    <a href="#" className="dropdown-item">
                                        <i className="fa fa-copy me-2"></i>Dupliquer
                                    </a>
                                    <a href="#" className="dropdown-item">
                                        <i className="fa fa-share me-2"></i>Partager
                                    </a>
                                    <a href="#" className="dropdown-item">
                                        <i className="fa fa-archive me-2"></i>Archiver
                                    </a>
                                    <div className="dropdown-divider"></div>
                                    <a href="#" className="dropdown-item text-danger">
                                        <i className="fa fa-trash me-2"></i>Supprimer
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CV Card 3 - Template Classique */}
                <div className="cv-card">
                    <div className="cv-card-header">
                        <div className="cv-preview classic">
                            <div className="cv-preview-content">
                                <div className="cv-header-preview classic">
                                    <div className="cv-avatar classic">BG</div>
                                    <div className="cv-info">
                                        <div className="cv-name">Babacar GUEYE</div>
                                        <div className="cv-title">Chef de Projet</div>
                                    </div>
                                </div>
                                <div className="cv-sections classic">
                                    <div className="cv-section"></div>
                                    <div className="cv-section"></div>
                                    <div className="cv-section short"></div>
                                </div>
                            </div>
                        </div>
                        <div className="cv-badges">
                            <span className="cv-badge info">Archivé</span>
                        </div>
                    </div>
                    <div className="cv-card-body">
                        <div className="cv-meta">
                            <h3 className="cv-name">Liasse Classique Pro</h3>
                            <p className="cv-description">Template professionnel classique</p>
                            <div className="cv-stats">
                                <div className="cv-stat">
                                    <i className="fa fa-eye"></i>
                                    <span>156 vues</span>
                                </div>
                                <div className="cv-stat">
                                    <i className="fa fa-download"></i>
                                    <span>8 téléchargements</span>
                                </div>
                                <div className="cv-stat">
                                    <i className="fa fa-calendar"></i>
                                    <span>Créé il y a 3 semaines</span>
                                </div>
                            </div>
                        </div>
                        <div className="cv-actions">
                            <button className="cv-action-btn primary">
                                <i className="fa fa-edit"></i>
                                Modifier
                            </button>
                            <button className="cv-action-btn secondary">
                                <i className="fa fa-download"></i>
                                Télécharger
                            </button>
                            <div className="cv-more-actions">
                                <button className="cv-more-btn">
                                    <i className="fa fa-ellipsis-v"></i>
                                </button>
                                <div className="cv-dropdown">
                                    <a href="#" className="dropdown-item">
                                        <i className="fa fa-copy me-2"></i>Dupliquer
                                    </a>
                                    <a href="#" className="dropdown-item">
                                        <i className="fa fa-share me-2"></i>Partager
                                    </a>
                                    <a href="#" className="dropdown-item">
                                        <i className="fa fa-archive me-2"></i>Archiver
                                    </a>
                                    <div className="dropdown-divider"></div>
                                    <a href="#" className="dropdown-item text-danger">
                                        <i className="fa fa-trash me-2"></i>Supprimer
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Create New CV Card */}
                <div className="cv-card create-new">
                    <div className="create-cv-content">
                        <div className="create-cv-icon">
                            <i className="fa fa-plus"></i>
                        </div>
                        <h3>Créer un nouveau Liasse</h3>
                        <p>Choisissez un template et créez votre Liasse en quelques minutes</p>
                        <button className="create-cv-button">
                            <i className="fa fa-magic me-2"></i>
                            Commencer
                        </button>
                    </div>
                </div>
            </div>
            </div>

            {/* Templates Section (hidden as requested) */}
            <div className="templates-section" style={{display:'none'}}>
                <div className="section-header">
                    <h2 className="section-title">
                        Templates Recommandés
                        <div className="title-underline"></div>
                    </h2>
                    <p className="section-subtitle">Choisissez parmi nos templates professionnels</p>
                </div>
                <div className="templates-grid">
                    <div className="template-card">
                        <div className="template-preview modern">
                            <div className="template-overlay">
                                <button className="template-btn">
                                    <i className="fa fa-eye me-2"></i>Aperçu
                                </button>
                                <button className="template-btn primary">
                                    <i className="fa fa-plus me-2"></i>Utiliser
                                </button>
                            </div>
                        </div>
                        <div className="template-info">
                            <h4>Moderne Pro</h4>
                            <p>Parfait pour les métiers tech</p>
                            <div className="template-tags">
                                <span className="tag">Moderne</span>
                                <span className="tag">Tech</span>
                            </div>
                        </div>
                    </div>
                    <div className="template-card">
                        <div className="template-preview creative">
                            <div className="template-overlay">
                                <button className="template-btn">
                                    <i className="fa fa-eye me-2"></i>Aperçu
                                </button>
                                <button className="template-btn primary">
                                    <i className="fa fa-plus me-2"></i>Utiliser
                                </button>
                            </div>
                        </div>
                        <div className="template-info">
                            <h4>Créatif Design</h4>
                            <p>Idéal pour les créatifs</p>
                            <div className="template-tags">
                                <span className="tag">Créatif</span>
                                <span className="tag">Design</span>
                            </div>
                        </div>
                    </div>
                    <div className="template-card">
                        <div className="template-preview classic">
                            <div className="template-overlay">
                                <button className="template-btn">
                                    <i className="fa fa-eye me-2"></i>Aperçu
                                </button>
                                <button className="template-btn primary">
                                    <i className="fa fa-plus me-2"></i>Utiliser
                                </button>
                            </div>
                        </div>
                        <div className="template-info">
                            <h4>Classique Pro</h4>
                            <p>Traditionnel et élégant</p>
                            <div className="template-tags">
                                <span className="tag">Classique</span>
                                <span className="tag">Pro</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResumesPage;
